import { useState, useEffect } from "react";
import { FaMobileAlt, FaUniversity } from "react-icons/fa";

const currencyFlags = {
  EUR: "🇪🇺",
  USD: "🇺🇸",
  XAF: "🇨🇲",
  CAD: "🇨🇦",
};

const currencyRates = {
  EUR: { USD: 1.1538, XAF: 655.957, CAD: 1.45 },
  USD: { EUR: 0.87, XAF: 600, CAD: 1.32 },
  XAF: { EUR: 0.0015, USD: 0.0017, CAD: 0.002273 },
  CAD: { EUR: 0.69, USD: 0.76, XAF: 415 },
};

const FLAT_FEE_CAD = 4;

function convertCadToCurrency(cadAmount, targetCurrency) {
  if (targetCurrency === "CAD") return cadAmount;
  const r = currencyRates?.CAD?.[targetCurrency];
  if (!r) return cadAmount;
  return cadAmount * r;
}

export default function TransferSimulator() {
  const [sendAmount, setSendAmount] = useState(10);
  const [sendCurrency, setSendCurrency] = useState("XAF");
  const [receiveCurrency, setReceiveCurrency] = useState("CAD");
  const [paymentMethod, setPaymentMethod] = useState("bank");
  const [exchangeRate, setExchangeRate] = useState(1);

  const [fee, setFee] = useState(0); // applied fee (promo) => ALWAYS 0
  const [originalFee, setOriginalFee] = useState(0); // normal fee (for strike-through)
  const [promoApplied, setPromoApplied] = useState(false);

  const [arrivalDate, setArrivalDate] = useState("");

  useEffect(() => {
    const rate = currencyRates[sendCurrency]?.[receiveCurrency] || 1;
    setExchangeRate(rate);

    const amount = Number(sendAmount || 0);

    // Normal policy (for display): < 200 => 4 CAD converted, else 2%
    const isBelowThreshold = amount <= 200;
    const normal = isBelowThreshold
      ? convertCadToCurrency(FLAT_FEE_CAD, sendCurrency)
      : amount * 0.02;

    // Promo ALWAYS: fee is 0 for any amount
    const applied = 0;

    setOriginalFee(Number(normal.toFixed(2)));
    setFee(Number(applied.toFixed(2)));
    setPromoApplied(amount > 0);

    // Arrival date rule:
    // - Any country -> Cameroon (XAF): same day
    // - Else: +2 days
    const isToCameroon = receiveCurrency === "XAF";
    const arrival = new Date();
    arrival.setDate(arrival.getDate() + (isToCameroon ? 0 : 2));

    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    setArrivalDate(arrival.toLocaleDateString("fr-FR", options));
  }, [sendAmount, sendCurrency, receiveCurrency]);

  // Keep simulator behavior: subtract applied fee from send amount then convert (fee=0 so unchanged)
  const convertedAmount = (Number(sendAmount || 0) - fee) * exchangeRate;

  return (
    <section
      className="font-roboto rounded-2xl border border-black/5 dark:border-white/10
                 bg-white dark:bg-slate-900 shadow-sm p-5"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">
            Simulateur de transfert
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Vérifiez le montant reçu, les frais et l’arrivée estimée
          </p>
        </div>

        <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 px-3 py-2">
          <p className="text-xs font-medium text-slate-700 dark:text-slate-200">
            1 {sendCurrency} = {exchangeRate.toFixed(4)} {receiveCurrency}
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Send */}
        <Field label="Vous envoyez">
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={sendAmount}
              onChange={(e) => setSendAmount(Number(e.target.value))}
              className="w-full bg-transparent outline-none text-lg font-semibold text-slate-900 dark:text-white"
              min={0.1}
              step={0.1}
            />
            <CurrencySelect value={sendCurrency} onChange={setSendCurrency} />
          </div>
        </Field>

        {/* Receive */}
        <Field label="Le bénéficiaire reçoit">
          <div className="flex items-center gap-3">
            <input
              type="text"
              readOnly
              value={Number.isFinite(convertedAmount) ? convertedAmount.toFixed(2) : "0.00"}
              className="w-full bg-transparent outline-none text-lg font-semibold text-emerald-700 dark:text-emerald-300"
            />
            <CurrencySelect value={receiveCurrency} onChange={setReceiveCurrency} />
          </div>
        </Field>
      </div>

      {/* Payment method */}
      <div className="mt-4">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
          Payer avec
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PayMethod
            active={paymentMethod === "bank"}
            icon={<FaUniversity />}
            title="Virement bancaire"
            subtitle="Paiement par banque"
            onClick={() => setPaymentMethod("bank")}
          />
          <PayMethod
            active={paymentMethod === "mobile"}
            icon={<FaMobileAlt />}
            title="Mobile Money"
            subtitle="Paiement mobile"
            onClick={() => setPaymentMethod("mobile")}
          />
        </div>
      </div>

      {/* Summary */}
      <div className="mt-5 rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 text-sm">
        <div className="flex items-center justify-between text-slate-700 dark:text-slate-200">
          <span>Frais</span>

          {promoApplied ? (
            <div className="text-right">
              <div className="text-sm">
                <span className="line-through text-red-600 font-semibold">
                  {originalFee.toFixed(2)} {sendCurrency}
                </span>
                <span className="ml-2 text-emerald-600 font-bold">
                  {fee.toFixed(2)} {sendCurrency}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                Promo appliquée
              </div>
            </div>
          ) : (
            <strong className="text-emerald-600 font-bold">
              {fee.toFixed(2)} {sendCurrency}
            </strong>
          )}
        </div>

        <div className="mt-1 flex items-center justify-between text-slate-600 dark:text-slate-300">
          <span>Total des frais inclus</span>
          <span>{sendAmount ? ((fee / Number(sendAmount)) * 100).toFixed(2) : "0.00"}%</span>
        </div>

        <div className="mt-1 flex items-center justify-between text-slate-600 dark:text-slate-300">
          <span>Arrivée estimée</span>
          <span className="font-medium">{arrivalDate}</span>
        </div>

        <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
          Promo: frais 0.00 quel que soit le montant. Les frais normaux sont affichés à titre indicatif.
        </p>
      </div>

      {/* Actions */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 py-3 font-medium text-slate-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition">
          Comparez les frais
        </button>
        <button className="rounded-2xl bg-slate-900 text-white py-3 font-medium hover:opacity-95 transition dark:bg-white dark:text-slate-900">
          Envoyez de l&apos;argent
        </button>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-950 p-4">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}

function CurrencySelect({ value, onChange }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 px-3 py-2">
      <span className="text-sm">{currencyFlags[value]}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent outline-none text-sm text-slate-700 dark:text-slate-200"
      >
        {Object.keys(currencyFlags).map((cur) => (
          <option key={cur} value={cur}>
            {cur}
          </option>
        ))}
      </select>
    </div>
  );
}

function PayMethod({ active, icon, title, subtitle, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 transition flex items-center gap-4
        ${
          active
            ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900"
            : "border-black/5 dark:border-white/10 bg-white dark:bg-slate-950 hover:bg-black/5 dark:hover:bg-white/10"
        }`}
    >
      <div
        className={`h-11 w-11 rounded-2xl flex items-center justify-center
        ${active ? "bg-white/15 dark:bg-black/10" : "bg-slate-100 dark:bg-white/10"}`}
      >
        <span className={`${active ? "text-white dark:text-slate-900" : "text-slate-700 dark:text-slate-200"}`}>
          {icon}
        </span>
      </div>
      <div className="min-w-0">
        <div className="font-semibold truncate">{title}</div>
        <div className={`text-sm ${active ? "text-white/80 dark:text-slate-600" : "text-slate-500 dark:text-slate-400"}`}>
          {subtitle}
        </div>
      </div>
    </button>
  );
}
